import React from "react";
import { useForm } from "react-hook-form";
import { CountrySelect } from "../../shared/CountrySelect/CountrySelect";
import { TextField } from "../../shared/TextField/TextField";
import { phoneValidator } from "../../../utils/phoneValidator";
import { zipCodeValidator } from "../../../utils/zipCodeValidator";

export interface NameLocationFormData {
  firstName: string;
  middleName?: string;
  lastName: string;
  address: {
    street: string;
    country: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
  };
}

interface NameLocationStepProps {
  onNext: (data: NameLocationFormData) => void;
  initialData?: NameLocationFormData;
}

export const NameLocationStep: React.FC<NameLocationStepProps> = ({
  onNext,
  initialData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NameLocationFormData>({
    defaultValues: initialData,
  });

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <TextField
          {...register("firstName", { required: "First name is required" })}
          label="First Name"
          id="firstName"
          errorMessage={errors.firstName?.message}
        />

        <TextField
          {...register("middleName")}
          label="Middle Name"
          id="middleName"
        />

        <TextField
          {...register("lastName", { required: "Last name is required" })}
          label="Last Name"
          id="lastName"
          errorMessage={errors.lastName?.message}
        />

        <TextField
          {...register("address.street", {
            required: "Street address is required",
          })}
          label="Street Address"
          id="streetAddress"
          errorMessage={errors.address?.street?.message}
        />

        <CountrySelect
          {...register("address.country", { required: "Country is required" })}
          errorMessage={errors.address?.country?.message}
        />

        <TextField
          {...register("address.city", { required: "City is required" })}
          label="City"
          id="city"
          errorMessage={errors.address?.city?.message}
        />

        {/* Probably we need to use a dropdown in here */}
        <TextField
          {...register("address.state", { required: "State is required" })}
          label="State"
          id="state"
          errorMessage={errors.address?.state?.message}
        />

        <TextField
          {...register("address.zipCode", {
            required: "ZIP code is required",
            validate: (value) => {
              const { errorText } = zipCodeValidator(value);

              return errorText ? errorText : true;
            },
          })}
          label="ZIP Code"
          id="zipCode"
          errorMessage={errors.address?.zipCode?.message}
        />

        <TextField
          {...register("address.phone", {
            required: "Phone is required",
            validate: (value) => {
              const validation = phoneValidator(value);

              return validation.errorText ? validation.errorText : true;
            },
          })}
          label="Phone"
          id="phone"
          errorMessage={errors.address?.phone?.message}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Next Step
        </button>
      </div>
    </form>
  );
};
